import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, FileText, Sparkles, Upload, User, Briefcase, MapPin } from 'lucide-react';

import { supabase } from '../../lib/supabase';
import { useUserProfile } from '../dashboard/hooks/useUserProfile';
import { langchainResumeParser } from '../../services/langchainService';
import { isOnboardingComplete, shouldBackfillOnboardingFlag } from './onboardingGate';
import LoadingSpinner from '../../components/LoadingSpinner';

// ──────────────────────────────────────────────────────────────────────────────
// Resume-First Onboarding Wizard (2 steps)
//
// Step 1: Upload resume (drag & drop) — instant "Aha!" moment
// Step 2: Confirm auto-parsed profile → done
// ──────────────────────────────────────────────────────────────────────────────

export interface OnboardingWizardProps {
  onComplete: () => void;
}

interface ParsedProfile {
  displayName: string;
  currentRole: string;
  experienceYears: string;
  skills: string[];
  professionalSummary: string;
  resumeRawText: string;
}

const EMPTY_PARSED: ParsedProfile = {
  displayName: '',
  currentRole: '',
  experienceYears: '',
  skills: [],
  professionalSummary: '',
  resumeRawText: '',
};

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const { profile, isLoading, save } = useUserProfile();

  const [step, setStep] = useState<1 | 2>(1);
  const [parsed, setParsed] = useState<ParsedProfile>({ ...EMPTY_PARSED });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from existing profile
  useEffect(() => {
    if (hydrated || isLoading) return;
    setHydrated(true);
    if (profile) {
      setParsed(prev => ({
        ...prev,
        displayName: profile.displayName || '',
        currentRole: profile.currentRole || '',
        experienceYears: profile.experienceYears?.toString() || '',
        skills: profile.skills || [],
      }));
    }
  }, [hydrated, isLoading, profile]);

  // Legacy / returning users — skip wizard
  useEffect(() => {
    if (!hydrated || isLoading) return;
    if (!isOnboardingComplete(profile)) return;
    if (shouldBackfillOnboardingFlag(profile)) {
      void save({ onboardingCompleted: true }).finally(onComplete);
      return;
    }
    onComplete();
  }, [hydrated, isLoading, profile, onComplete, save]);

  // Get user's name from auth metadata
  const authName = useMemo(() => {
    const meta = supabase.auth as unknown as { currentSession?: { user?: { user_metadata?: { full_name?: string; name?: string } } } };
    return '';
  }, []);

  // ── Resume upload handler ────────────────────────────────────────────────
  const handleFileUpload = useCallback(async (file: File) => {
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|docx?|txt)$/i)) {
      setUploadError('Please upload a PDF, DOCX, or TXT file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const result = await langchainResumeParser.parseResume(file);
      if (!result.rawText || result.rawText.length < 50) {
        throw new Error('Could not extract text from the file. Please try a different format.');
      }

      const structured = result.structured || {};

      /** Safely extract a string from any parsed value (object, array, or string). */
      const toText = (v: unknown): string => {
        if (typeof v === 'string') return v;
        if (v == null) return '';
        if (typeof v === 'number' || typeof v === 'boolean') return String(v);
        if (typeof v === 'object') {
          const obj = v as Record<string, unknown>;
          if ('text' in obj && typeof obj.text === 'string') return obj.text;
          if ('name' in obj && typeof obj.name === 'string') return obj.name;
          if ('title' in obj && typeof obj.title === 'string') return obj.title;
          if ('value' in obj && typeof obj.value === 'string') return obj.value;
          // Last resort: join all string values
          const vals = Object.values(obj).filter(x => typeof x === 'string') as string[];
          if (vals.length > 0) return vals.join(' ');
        }
        return '';
      };

      const extractLines = (key: string): string[] => {
        const val = structured[key];
        if (!val) return [];
        if (typeof val === 'string') return [val];
        if (Array.isArray(val)) return val.map(toText).filter(Boolean);
        return [toText(val)].filter(Boolean);
      };

      const skillLines = extractLines('Skills');
      const skills = skillLines.flatMap(s => s.split(/[,;|]/).map(sk => sk.trim()).filter(Boolean));
      const summaryLines = extractLines('Professional Summary');
      const expLines = extractLines('Work Experience');

      // Try to extract name from resume
      let name = parsed.displayName || profile?.displayName || '';
      if (!name && structured['Personal Info']) {
        const pi = structured['Personal Info'];
        if (Array.isArray(pi) && pi.length > 0) name = toText(pi[0]).trim();
        else name = toText(pi).trim();
      }

      // Try to extract role from experience
      let role = parsed.currentRole || profile?.currentRole || '';
      if (!role && expLines.length > 0) {
        role = expLines[0].split(/\bat\b|[-–—]/)[0]?.trim() || '';
      }

      // Sanitize: ensure all values are plain strings, not objects
      const safeName = typeof name === 'string' ? name : toText(name);
      const safeRole = typeof role === 'string' ? role : toText(role);
      const safeSkills = skills.filter(s => typeof s === 'string' && s.trim() && !s.startsWith('[object'));

      setParsed({
        displayName: safeName,
        currentRole: safeRole,
        experienceYears: parsed.experienceYears || profile?.experienceYears?.toString() || '',
        skills: safeSkills.length > 0 ? safeSkills : (parsed.skills.length > 0 ? parsed.skills : []),
        professionalSummary: summaryLines.join(' '),
        resumeRawText: result.rawText,
      });

      setStep(2);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to parse resume');
    } finally {
      setUploading(false);
    }
  }, [parsed, profile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFileUpload(file);
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFileUpload(file);
  }, [handleFileUpload]);

  // ── Skip resume upload → go to step 2 with manual entry ──────────────────
  const handleSkipUpload = useCallback(() => {
    // Pre-fill from Google auth metadata
    const name = profile?.displayName || '';
    setParsed(prev => ({
      ...prev,
      displayName: name,
    }));
    setStep(2);
  }, [profile]);

  // ── Finish onboarding ───────────────────────────────────────────────────
  const handleFinish = useCallback(async () => {
    // Minimal validation
    if (!parsed.displayName.trim()) {
      setUploadError('Please enter your name.');
      return;
    }

    setFinishing(true);
    try {
      await save({
        displayName: parsed.displayName.trim(),
        currentRole: parsed.currentRole.trim() || undefined,
        experienceYears: parsed.experienceYears ? Number(parsed.experienceYears) : undefined,
        skills: parsed.skills.length > 0 ? parsed.skills : undefined,
        professionalSummary: parsed.professionalSummary || undefined,
        resumeRawText: parsed.resumeRawText || undefined,
        resumeSource: parsed.resumeRawText ? 'upload' : undefined,
        onboardingCompleted: true,
      });

      // Best-effort profile vector upsert
      try {
        await supabase.functions.invoke('upsert-profile-vector');
      } catch { /* non-blocking */ }

      sessionStorage.setItem('onboarding_just_completed', Date.now().toString());
      onComplete();
    } catch {
      setUploadError('Could not save your profile. Please try again.');
    } finally {
      setFinishing(false);
    }
  }, [parsed, save, onComplete]);

  // ── Loading state ───────────────────────────────────────────────────────
  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--ds-bg-app)' }}>
        <LoadingSpinner size="lg" showText />
      </div>
    );
  }

  // ── STEP 1: Resume Upload ───────────────────────────────────────────────
  if (step === 1) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: 'var(--ds-bg-app)' }}>
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--ds-border)' }}>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-green-600" />
            <span className="font-bold text-sm text-gray-900 dark:text-white">GoGrabJob</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-1 rounded-full bg-green-500" />
            <div className="w-8 h-1 rounded-full" style={{ background: 'var(--ds-border)' }} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-lg text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center bg-green-500/10">
              <FileText className="w-10 h-10 text-green-600" />
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold mb-3 text-gray-900 dark:text-white">
              {t('onboarding.resume.heading', 'Upload your resume to get started')}
            </h1>
            <p className="text-base text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto leading-relaxed">
              {t('onboarding.resume.subheading', 'We\'ll auto-fill your profile, match you with jobs, and score your resume — all in under 15 seconds.')}
            </p>

            {/* Drag & drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="relative mb-4"
            >
              <label
                className={[
                  'flex flex-col items-center justify-center gap-3 w-full p-8 sm:p-10',
                  'border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-200',
                  'hover:border-green-500 hover:bg-green-500/5',
                  uploading ? 'border-green-500 bg-green-500/5' : 'border-gray-300 dark:border-gray-600',
                ].join(' ')}
              >
                {uploading ? (
                  <>
                    <LoadingSpinner size="lg" showText={false} />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      {t('onboarding.resume.parsing', 'Parsing your resume with AI...')}
                    </span>
                  </>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                    <span className="text-base font-semibold text-gray-700 dark:text-gray-200">
                      {t('onboarding.resume.dropzoneTitle', 'Drop your resume here or click to browse')}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      PDF, DOCX, or TXT · Max 5MB
                    </span>
                  </>
                )}
                <input
                  type="file"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={handleInputChange}
                  disabled={uploading}
                />
              </label>
            </div>

            {uploadError && (
              <p className="text-sm text-red-500 mb-4">{uploadError}</p>
            )}

            {/* Skip link */}
            <button
              type="button"
              onClick={handleSkipUpload}
              className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              {t('onboarding.resume.skipLink', 'Skip for now — I\'ll fill in my details manually')}
            </button>

            {/* Value props */}
            <div className="mt-10 grid grid-cols-3 gap-4 text-center max-w-md mx-auto">
              {[
                { icon: '⚡', label: 'AI-parsed profile' },
                { icon: '🎯', label: 'Matched jobs instantly' },
                { icon: '📊', label: 'ATS score in seconds' },
              ].map((item) => (
                <div key={item.label} className="flex flex-col items-center gap-1.5">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 2: Confirm Profile ─────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--ds-bg-app)' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--ds-border)' }}>
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-green-600" />
          <span className="font-bold text-sm text-gray-900 dark:text-white">GoGrabJob</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-8 h-1 rounded-full bg-green-500" />
          <div className="w-8 h-1 rounded-full bg-green-500" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          {parsed.resumeRawText ? (
            <div className="flex items-center gap-2 mb-6 px-3 py-2 rounded-lg bg-green-500/10">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
              <span className="text-sm font-medium text-green-700 dark:text-green-400">
                Resume parsed successfully — review your details below
              </span>
            </div>
          ) : null}

          <h2 className="text-xl sm:text-2xl font-bold mb-1 text-gray-900 dark:text-white">
            {t('onboarding.confirm.heading', 'Confirm your profile')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('onboarding.confirm.subheading', 'Quick check — you can always edit later.')}
          </p>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                <User className="w-3.5 h-3.5" /> Your name *
              </label>
              <input
                type="text"
                value={parsed.displayName}
                onChange={(e) => { setParsed(p => ({ ...p, displayName: e.target.value })); setUploadError(null); }}
                placeholder="e.g. Dharmendra Singh"
                className="w-full h-11 px-3 rounded-xl text-sm border transition-colors focus:outline-none focus:ring-2 focus:ring-green-500/30"
                style={{
                  background: 'var(--ds-bg-elevated, #fff)',
                  borderColor: 'var(--ds-border)',
                  color: 'var(--ds-text-body)',
                }}
              />
            </div>

            {/* Current role */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                <Briefcase className="w-3.5 h-3.5" /> Current role
              </label>
              <input
                type="text"
                value={parsed.currentRole}
                onChange={(e) => setParsed(p => ({ ...p, currentRole: e.target.value }))}
                placeholder="e.g. Senior Software Engineer"
                className="w-full h-11 px-3 rounded-xl text-sm border transition-colors focus:outline-none focus:ring-2 focus:ring-green-500/30"
                style={{
                  background: 'var(--ds-bg-elevated, #fff)',
                  borderColor: 'var(--ds-border)',
                  color: 'var(--ds-text-body)',
                }}
              />
            </div>

            {/* Experience years */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                <MapPin className="w-3.5 h-3.5" /> Years of experience
              </label>
              <input
                type="number"
                value={parsed.experienceYears}
                onChange={(e) => setParsed(p => ({ ...p, experienceYears: e.target.value }))}
                placeholder="e.g. 5"
                min={0}
                max={60}
                className="w-full h-11 px-3 rounded-xl text-sm border transition-colors focus:outline-none focus:ring-2 focus:ring-green-500/30"
                style={{
                  background: 'var(--ds-bg-elevated, #fff)',
                  borderColor: 'var(--ds-border)',
                  color: 'var(--ds-text-body)',
                }}
              />
            </div>

            {/* Skills */}
            {parsed.skills.length > 0 && (
              <div>
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 block">
                  Skills ({parsed.skills.length})
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {parsed.skills.slice(0, 12).map((skill, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-700 dark:text-green-400"
                    >
                      {skill}
                    </span>
                  ))}
                  {parsed.skills.length > 12 && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500">
                      +{parsed.skills.length - 12} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {uploadError && (
            <p className="text-sm text-red-500 mt-3">{uploadError}</p>
          )}

          {/* CTA */}
          <button
            type="button"
            onClick={handleFinish}
            disabled={finishing}
            className="w-full mt-8 min-h-[48px] rounded-xl font-bold text-base text-white transition-all duration-200 hover:shadow-lg active:scale-[0.98] disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #5F8568, #4a7a54)' }}
          >
            {finishing ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" showText={false} />
                Setting up your workspace...
              </span>
            ) : (
              t('onboarding.confirm.cta', 'Confirm & Find My Jobs →')
            )}
          </button>

          {/* Back to upload */}
          <button
            type="button"
            onClick={() => { setStep(1); setUploadError(null); }}
            className="w-full mt-3 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            ← Back to resume upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
